import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import styled from "styled-components/macro";

import { useHasHideMakeAPaymentButton } from "Flocknote/stateHooks";

import { useManageMyGivingPage } from "Giving/ManageMyGivingPage/ManageMyGivingPageContext";
import { useAccentColor } from "Giving/ManageMyGivingPage/stateHooks";
import classNames from "classnames";
import { motion } from "framer-motion";

import FnrButton, { ButtonIcon, ButtonText } from "UI/FnrButton3";
import FnrIcon from "UI/FnrIcon";
import FnrOverlay from "UI/FnrOverlay";

import Fund from "./Fund";

const INITIAL_FUNDS_MAX = 5;

export default function PublicFundList() {
  const { state } = useManageMyGivingPage();
  const { pageSettings, callToActions, alerts } = state;
  const [hasHideMakeAPaymentButton] = useHasHideMakeAPaymentButton();

  const [showFundsList, setShowFundsList] = useState(false);
  const [shouldShow, setShouldShow] = useState(showFundsList);
  const accentColor = useAccentColor();

  const shouldShowFundsListInsteadOfButton =
    !pageSettings?.description &&
    !pageSettings?.imageURL &&
    !pageSettings?.videoURL &&
    callToActions?.length === 0 &&
    alerts?.length === 0;

  const toggleShowFundsList = () => setShowFundsList(!showFundsList);

  const determineIfShouldPlaceInOverlay = () => window.innerHeight >= 600;

  const [placeInOverlay, setPlaceInOverlay] = useState(
    determineIfShouldPlaceInOverlay()
  );

  useEffect(() => {
    if (showFundsList) {
      setPlaceInOverlay(determineIfShouldPlaceInOverlay());
      setShouldShow(true);
    } else setShouldShow(false);
  }, [showFundsList]);

  if (shouldShowFundsListInsteadOfButton) {
    return (
      <MakeAGiftHolder>
        <InnerComponent show={true} hide={toggleShowFundsList} />
      </MakeAGiftHolder>
    );
  }

  if (hasHideMakeAPaymentButton ?? true) return null;

  return (
    <MakeAGiftHolder>
      <MakeAGiftButtonHolder
        animate={
          !placeInOverlay && shouldShow
            ? { height: 0, opacity: 0, scale: 0.8 }
            : null
        }
      >
        <FnrButton
          color={accentColor}
          onClick={toggleShowFundsList}
          size="large"
        >
          <ButtonText>Make a Gift</ButtonText>
          <ButtonIcon fas fa-hand-holding-usd />
        </FnrButton>
      </MakeAGiftButtonHolder>

      {placeInOverlay ? (
        <FnrOverlay
          show={shouldShow}
          style={{
            background:
              "linear-gradient(140deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.4) 100%)",
            borderRadius: 24,
          }}
        >
          <InnerComponent
            show={shouldShow}
            hide={toggleShowFundsList}
            inOverlay
          />
        </FnrOverlay>
      ) : (
        <InnerComponent show={shouldShow} hide={toggleShowFundsList} />
      )}
    </MakeAGiftHolder>
  );
}

const MakeAGiftHolder = styled.div`
  order: 3;
  grid-area: gift;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const MakeAGiftButtonHolder = styled(motion.div)`
  position: relative;
  z-index: 1;
`;

InnerComponent.propTypes = {
  show: PropTypes.bool,
  hide: PropTypes.func,
  inOverlay: PropTypes.bool,
};

function InnerComponent({ show, hide, inOverlay }) {
  const { state } = useManageMyGivingPage();
  const { funds } = state;
  const [showAll, setShowAll] = useState(false);

  const displayShowAllButton = funds.length > INITIAL_FUNDS_MAX;

  const topFunds = funds.slice(0, INITIAL_FUNDS_MAX);
  const bottomFunds = funds.slice(INITIAL_FUNDS_MAX);

  useEffect(() => {
    if (!show) setTimeout(() => setShowAll(false), 500);
  }, [show]);

  return (
    <Wrapper
      className={classNames({ in_overlay: inOverlay })}
      initial={inOverlay ? null : { height: 0, scale: 0.8 }}
      animate={
        inOverlay
          ? null
          : show
          ? { height: "auto", scale: 1 }
          : { height: 0, scale: 0.9 }
      }
    >
      {inOverlay && (
        <CloseButton
          whileTap={{ scale: 1 }}
          whileHover={{ opacity: 1, scale: 1.1 }}
          onClick={hide}
        >
          <FnrIcon fas fa-times-circle />
        </CloseButton>
      )}
      <Title>
        <div>Where would you like to direct your gift?</div>
        <Icon>
          <FnrIcon fas fa-hand-holding-usd />
        </Icon>
      </Title>

      <FundListWrapper>
        {topFunds.map(
          ({ ID, description: name, fundColor, paymentType }, i) => (
            <Fund
              key={ID}
              ID={ID}
              name={name}
              fundColor={fundColor}
              paymentType={paymentType}
            />
          )
        )}
        <MoreFundsList
          initial={{ height: 0, opacity: 0 }}
          animate={
            showAll ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }
          }
        >
          {bottomFunds.map(
            ({ ID, description: name, fundColor, paymentType }, i) => (
              <Fund
                key={ID}
                ID={ID}
                name={name}
                fundColor={fundColor}
                paymentType={paymentType}
              />
            )
          )}
        </MoreFundsList>
        {displayShowAllButton ? (
          <ShowAllWrapper
            initial={{ height: "auto", opacity: 1 }}
            animate={
              !showAll
                ? { height: "auto", opacity: 1 }
                : { height: 0, opacity: 0 }
            }
          >
            <ShowAllButton>
              <Fund fundColor="FFFFFF" onClick={() => setShowAll(true)}>
                <ShowAllButtonInside>
                  <div>Show More</div>
                  <FnrIcon fas fa-chevron-down />
                </ShowAllButtonInside>
              </Fund>
            </ShowAllButton>
          </ShowAllWrapper>
        ) : null}
      </FundListWrapper>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  box-sizing: border-box;
  color: #fff;
  width: 340px;
  margin: 0 auto;
  position: relative;
  z-index: 100;

  &:not(.in_overlay) {
    overflow: hidden;
    background: linear-gradient(
      140deg,
      var(--bgGradient1) 0%,
      var(--bgGradient2) 100%
    );
    border-radius: 24px;
  }
`;
const CloseButton = styled(motion.div)`
  position: absolute;
  top: 4px;
  right: 7px;
  font-size: 22px;
  cursor: pointer;
  opacity: 0.5;
`;

const Title = styled.div`
  font-weight: 400;
  font-size: 20px;
  padding: 20px 28px;
  text-shadow: rgba(0, 0, 0, 0.6) 1px 1px 4px;
  display: flex;
  align-items: center;
  gap: 16px;
  line-height: 24px;
`;
const Icon = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  font-size: 36px;
`;

const FundListWrapper = styled.div`
  background: linear-gradient(
    140deg,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0.45) 100%
  );
  margin: 8px;
  margin-top: 0;
  border-radius: 16px;
  overflow: hidden;
  padding: 8px 0;
  padding-bottom: 6px;
  ${Wrapper}.in_overlay & {
    max-height: calc(80vh - 120px);
    overflow: auto;
  }
`;

const ShowAllWrapper = styled(motion.div)`
  overflow: hidden;
`;
const ShowAllButton = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 8px;
`;
const ShowAllButtonInside = styled.div`
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
`;
const MoreFundsList = styled(motion.div)`
  overflow: hidden;
`;
